import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useMemo, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getAccountBalance, getDashboardDisplayedCurrencies, getPendingBalance } from 'state/reducers/balance.reducer';
import { SmallCard } from 'theme/elements/Cards';
import { getAccountBalances, getCurrenciesToDisplay, getPendingBalances } from 'services/balances.service';
import _ from 'lodash';
import { format, startOfDay } from 'date-fns';
import {
	IBalanceTypeCategory,
	IBalanceDashboard,
	currenciesToDisplay,
	IFilterType,
	DashboardCategories,
} from 'constants/balance.constants';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { ICurrency } from 'constants/business-plan.constants';

interface TopBoxProps {
	category: IBalanceTypeCategory;
}

export const TopBox: React.FC<TopBoxProps> = ({ category }) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const [chosenCoinIndex, setChosenCoinIndex] = useState(0);
	const navigate = useNavigate();
	const accountBalances = useAppSelector(getAccountBalance);
	const pendingBalances = useAppSelector(getPendingBalance);
	const currencies: ICurrency[] = useAppSelector(getDashboardDisplayedCurrencies);
	const accountBalancePermission = hasPermission([permissionEnums.DASHBOARD_ACCOUNT_BALANCE]);
	const pendingBalancePermission = hasPermission([permissionEnums.DASHBOARD_PENDING_BALANCE]);

	const currentBalance = useMemo(() => {
		if (category.name === DashboardCategories.CUSTOMER_BALANCES) {
			return accountBalances;
		}
		if (category.name === DashboardCategories.PENDING_BALANCE) {
			return pendingBalances;
		}
	}, [accountBalances, pendingBalances, category.name]);

	const sortedBalances = useMemo(() => {
		return _.map(currencies, (currency) => {
			const balance = _.find(currentBalance, { currencyId: currency.id });
			return (
				balance || {
					currencyId: currency.id,
					sum: 0,
					pendingSum: 0,
				}
			);
		});
	}, [currentBalance, currencies]);

	const currentDate = useAppSelector((state) => _.get(state, 'dashboard.filters.datesRange.endDate'));
	const formattedDate = format(currentDate, 'dd.MM.yyyy');

	return (
		<Stack sx={{ position: 'relative' }}>
			{sortedBalances && (
				<SmallCard style={{ display: 'flex', flexDirection: 'column' }}>
					<Typography variant="captionRegular">
						{t('page.dashboard.dashboardBalances.asOfDate')} {formattedDate}
					</Typography>
					<Box
						sx={{ cursor: 'pointer' }}
						onClick={() => navigate('/' + pagesEnum.MAIN_DASHBOARD + '/' + category.name)}
					>
						{sortedBalances[chosenCoinIndex] && (
							<Typography variant="h5" sx={{ direction: 'rtl', textAlign: 'left' }}>
								{Number(sortedBalances[chosenCoinIndex]?.sum) >= 0 ? '' : '-'}
								{currencies[chosenCoinIndex].symbol}
								{formatNumber(Math.abs(Number(sortedBalances[chosenCoinIndex]?.sum)))}
							</Typography>
						)}
					</Box>
					<Box display="flex" alignItems="center" gap="10px">
						{category.icon && (
							<img src={`/icons/${category.icon}.svg`} width={16} height={16} alt={category.name} />
						)}
						<Typography variant="subtitle2">
							{t(`page.dashboard.dashboardBalances.${category.name}`)}
						</Typography>
					</Box>

					<Box style={{ display: 'flex', flexDirection: 'row' }}>
						{currencies?.map((coin, index) => {
							return (
								<Box
									key={index}
									style={{
										margin: '0 10px',
										backgroundColor: index === chosenCoinIndex ? '#E4F3EF' : 'transparent',
										cursor: 'pointer',
										padding: '5px',
										borderRadius: '4px',
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
									onClick={() => setChosenCoinIndex(index)}
								>
									<Typography variant="semiCaptionRegular" style={{ marginLeft: '5px' }}>
										{coin.symbol}
									</Typography>
									<Typography variant="semiCaptionRegular">{coin.code}</Typography>
								</Box>
							);
						})}
					</Box>
				</SmallCard>
			)}
		</Stack>
	);
};
